import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { NauticusLogo } from "@/components/NauticusLogo";
import { PoweredByFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-mist-100">
      <div className="w-full max-w-md">
        <div className="mb-8"><NauticusLogo /></div>
        <h1 className="font-display text-3xl font-bold text-navy-900 tracking-tight">Set a new password</h1>
        <p className="text-muted-foreground mt-2 mb-8">Choose a new password for your account.</p>

        {error && (
          <Alert variant="destructive" className="mb-5" data-testid="reset-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {done ? (
          <Alert className="mb-5 bg-white border-mist-200" data-testid="reset-done">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <AlertDescription>Password updated. Redirecting you to sign in…</AlertDescription>
          </Alert>
        ) : !token ? (
          <Alert variant="destructive" className="mb-5" data-testid="reset-no-token">
            <AlertDescription>This reset link is missing its token. Please request a new one.</AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="font-semibold">New password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters" className="mt-1.5 h-11 bg-white" data-testid="reset-password" />
            </div>
            <div>
              <Label htmlFor="confirm" className="font-semibold">Confirm password</Label>
              <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password" className="mt-1.5 h-11 bg-white" data-testid="reset-confirm" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full h-11 bg-navy-800 hover:bg-navy-700 text-white rounded-full font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
        )}

        <p className="text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-gold-dark font-semibold hover:underline" data-testid="reset-back-login">
            Back to sign in
          </Link>
        </p>
        <PoweredByFooter className="mt-10" />
      </div>
    </div>
  );
}
